import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, RotateCw } from 'lucide-react';
import { API_BASE } from '../services/apiBase';

const API    = API_BASE;
const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

function authHeaders() {
  return { Authorization: 'Bearer ' + localStorage.getItem('access_token') };
}

function fmtSecs(s) {
  if (!s) return '—';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const SLOT_TYPES = [
  { type: 'music',    label: 'Song',         color: '#2f81f7', bg: 'rgba(47,129,247,0.15)',  defaultDur: 210 },
  { type: 'ad',       label: 'Ad',           color: '#fb923c', bg: 'rgba(251,146,60,0.15)',  defaultDur: 30  },
  { type: 'djlink',   label: 'DJ Link',      color: '#2dd4bf', bg: 'rgba(45,212,191,0.15)',  defaultDur: 15  },
  { type: 'jingle',   label: 'Jingle',       color: '#a78bfa', bg: 'rgba(167,139,250,0.15)', defaultDur: 15  },
  { type: 'sweeper',  label: 'Sweeper',      color: '#f472b6', bg: 'rgba(244,114,182,0.15)', defaultDur: 10  },
  { type: 'announcement', label: 'Announce', color: '#22c55e', bg: 'rgba(34,197,94,0.15)',   defaultDur: 30  },
];

function slotMeta(type) {
  return SLOT_TYPES.find(s => s.type === type) || SLOT_TYPES[0];
}

function totalDuration(slots) {
  return slots.reduce((s, slot) => {
    return s + (slot.duration_seconds || slotMeta(slot.slot_type).defaultDur);
  }, 0);
}

// ── Palette block (drag source) ───────────────────────────────────────────────
function PaletteBlock({ meta, onAdd }) {
  return (
    <button
      onClick={onAdd}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '10px 12px', borderRadius: 8, border: `1px solid ${meta.color}30`,
        background: meta.bg, cursor: 'pointer', textAlign: 'left', marginBottom: 6,
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = meta.color + '80'}
      onMouseLeave={e => e.currentTarget.style.borderColor = meta.color + '30'}
    >
      <div style={{ width: 4, height: 28, borderRadius: 2, background: meta.color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: meta.color }}>{meta.label}</div>
        <div style={{ fontSize: 11, color: '#8b949e' }}>{fmtSecs(meta.defaultDur)}</div>
      </div>
      <Plus size={14} color={meta.color} style={{ marginLeft: 'auto', flexShrink: 0 }} />
    </button>
  );
}

// ── Timeline slot block ───────────────────────────────────────────────────────
function SlotBlock({ slot, index, isDragging, isOver, onDelete, onDragStart, onDragEnter, onDragEnd, categories }) {
  const meta = slotMeta(slot.slot_type);
  const cat  = categories.find(c => c.id === slot.category_id);
  const dur  = slot.duration_seconds || meta.defaultDur;

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragEnter={() => onDragEnter(index)}
      onDragEnd={onDragEnd}
      onDragOver={e => e.preventDefault()}
      style={{
        display: 'flex', alignItems: 'center', gap: 0,
        marginBottom: 4, opacity: isDragging ? 0.35 : 1,
        borderTop: isOver ? `2px solid ${meta.color}` : '2px solid transparent',
        transition: 'opacity 0.15s, border-color 0.1s',
        cursor: 'grab',
      }}
    >
      {/* Drag handle */}
      <div style={{ padding: '0 8px', color: '#484f58', fontSize: 14, userSelect: 'none', flexShrink: 0 }}>⋮⋮</div>

      {/* Block */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 10,
        background: '#161b22', border: `1px solid #30363d`,
        borderLeft: `3px solid ${meta.color}`,
        borderRadius: '0 8px 8px 0', padding: '10px 14px',
        minHeight: Math.max(40, Math.round(dur / 4)),
      }}>
        {/* Type badge */}
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
          background: meta.bg, color: meta.color, letterSpacing: '0.05em', flexShrink: 0,
        }}>{meta.label.toUpperCase()}</span>

        {/* Category */}
        <span style={{ fontSize: 13, color: cat ? '#e6edf3' : '#484f58', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cat ? cat.name : slot.label || <span style={{ fontStyle: 'italic' }}>No category</span>}
        </span>

        {/* Duration */}
        <span style={{ fontSize: 11, color: '#8b949e', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          {fmtSecs(dur)}
        </span>

        {/* Delete */}
        <button onClick={() => onDelete(slot.id)} style={{
          padding: '4px', borderRadius: 6, border: 'none', background: 'transparent',
          color: '#484f58', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
        onMouseLeave={e => e.currentTarget.style.color = '#484f58'}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Time marker column ────────────────────────────────────────────────────────
function TimeMarkers({ slots }) {
  let cum = 0;
  const markers = slots.map((slot, i) => {
    const m = { index: i, time: cum };
    cum += slot.duration_seconds || slotMeta(slot.slot_type).defaultDur;
    return m;
  });

  return (
    <div style={{ width: 52, flexShrink: 0, paddingTop: 2 }}>
      {markers.map(({ index, time }) => {
        const mins = Math.floor(time / 60), secs = time % 60;
        const label = mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : '0:00';
        return (
          <div key={index} style={{
            height: Math.max(44, Math.round((slots[index].duration_seconds || slotMeta(slots[index].slot_type).defaultDur) / 4) + 14),
            display: 'flex', alignItems: 'flex-start', paddingTop: 12,
          }}>
            <span style={{ fontSize: 10, color: '#484f58', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' }}>{label}</span>
          </div>
        );
      })}
      {slots.length > 0 && (
        <div style={{ paddingTop: 6 }}>
          <span style={{ fontSize: 10, color: '#2f81f7', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {fmtSecs(totalDuration(slots))}
          </span>
        </div>
      )}
    </div>
  );
}

// ── New clock modal ───────────────────────────────────────────────────────────
function NewClockModal({ storeId, onCreated, onClose }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/rotations/clocks`, {
        tenant_id: TENANT, store_id: storeId || null, name: name.trim(), description: '', color: '#2f81f7',
      }, { headers: authHeaders() });
      onCreated(r.data.id);
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, padding: 24, width: 360 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#e6edf3' }}>New Clock Template</h2>
        <input
          value={name} onChange={e => setName(e.target.value)}
          placeholder="e.g. Morning Drive, Weekend Mix…"
          autoFocus
          onKeyDown={e => e.key === 'Enter' && create()}
          style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: '9px 12px', color: '#e6edf3', fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 16 }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #30363d', background: 'transparent', color: '#8b949e', cursor: 'pointer' }}>Cancel</button>
          <button onClick={create} disabled={saving || !name.trim()} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: saving || !name.trim() ? '#21262d' : '#2f81f7', color: saving || !name.trim() ? '#484f58' : '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ClockBuilder() {
  const [stores, setStores]       = useState([]);
  const [storeId, setStoreId]     = useState('');
  const [clocks, setClocks]       = useState([]);
  const [clockId, setClockId]     = useState('');
  const [slots, setSlots]         = useState([]);
  const [categories, setCategories] = useState([]);
  const [addType, setAddType]     = useState('music');
  const [addCat, setAddCat]       = useState('');
  const [addDur, setAddDur]       = useState('');
  const [saving, setSaving]       = useState(false);
  const [dirty, setDirty]         = useState(false);
  const [showNewClock, setShowNewClock] = useState(false);
  const [dragIdx, setDragIdx]     = useState(null);
  const [overIdx, setOverIdx]     = useState(null);

  // Load stores
  useEffect(() => {
    axios.get(`${API}/tenants/${TENANT}/stores`, { headers: authHeaders() })
      .then(r => {
        const list = r.data?.items || r.data || [];
        setStores(list);
        if (list.length > 0) setStoreId(list[0].id);
      });
  }, []);

  // Load clocks when store changes
  const loadClocks = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const r = await axios.get(`${API}/rotations/clocks`, {
        params: { tenant_id: TENANT, store_id: sid }, headers: authHeaders(),
      });
      const list = r.data || [];
      setClocks(list);
      if (list.length > 0) setClockId(list[0].id);
      else setClockId('');
    } catch (e) { console.error(e); }
  }, []);

  // Load categories when store changes
  const loadCategories = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const r = await axios.get(`${API}/rotations/categories`, {
        params: { tenant_id: TENANT, store_id: sid }, headers: authHeaders(),
      });
      setCategories(r.data || []);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (storeId) { loadClocks(storeId); loadCategories(storeId); }
  }, [storeId, loadClocks, loadCategories]);

  // Load slots when clock changes
  const loadSlots = useCallback(async (cid) => {
    if (!cid) { setSlots([]); return; }
    try {
      const r = await axios.get(`${API}/rotations/clocks/${cid}/slots`, { headers: authHeaders() });
      setSlots((r.data || []).sort((a, b) => a.position - b.position));
      setDirty(false);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadSlots(clockId); }, [clockId, loadSlots]);

  // Add slot — append to local state to preserve unsaved drag order
  const addSlot = async () => {
    if (!clockId) return;
    const meta = slotMeta(addType);
    const dur = addDur ? Number(addDur) : meta.defaultDur;
    try {
      const r = await axios.post(`${API}/rotations/clocks/${clockId}/slots`, {
        slot_type: addType,
        category_id: addCat || null,
        duration_seconds: dur,
        label: '',
      }, { headers: authHeaders() });
      // Append to current local order without re-fetching (preserves unsaved drag order)
      setSlots(prev => [...prev, {
        id: r.data.id,
        slot_type: addType,
        category_id: addCat || null,
        duration_seconds: dur,
        label: '',
        position: prev.length + 1,
      }]);
      setDirty(true); // new slot needs its position saved
    } catch (e) { alert('Failed to add slot'); }
  };

  // Delete slot
  const deleteSlot = async (sid) => {
    await axios.delete(`${API}/rotations/clocks/${clockId}/slots/${sid}`, { headers: authHeaders() });
    setSlots(prev => prev.filter(s => s.id !== sid));
  };

  // Drag & drop handlers
  const onDragStart = (i) => setDragIdx(i);
  const onDragEnter = (i) => setOverIdx(i);
  const onDragEnd   = () => {
    if (dragIdx !== null && overIdx !== null && dragIdx !== overIdx) {
      const next = [...slots];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(overIdx, 0, moved);
      setSlots(next);
      setDirty(true);
    }
    setDragIdx(null);
    setOverIdx(null);
  };

  // Save reordered slots
  const saveOrder = async () => {
    if (!clockId || !dirty) return;
    setSaving(true);
    try {
      await axios.put(`${API}/rotations/clocks/${clockId}/slots/reorder`, {
        slot_ids: slots.map(s => s.id),
      }, { headers: authHeaders() });
      setDirty(false);
    } catch (e) { alert('Failed to save order'); }
    setSaving(false);
  };

  const totSecs = totalDuration(slots);
  const totMins = Math.floor(totSecs / 60);

  const iStyle = { background: '#161b22', border: '1px solid #30363d', borderRadius: 7, padding: '6px 10px', color: '#e6edf3', fontSize: 13, outline: 'none' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0d1117', color: '#e6edf3', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#e6edf3' }}>Clock Builder</h1>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8b949e' }}>
            Visual timeline — drag blocks to reorder, click type to add
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Store */}
          <select value={storeId} onChange={e => setStoreId(e.target.value)} style={iStyle}>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {/* Clock */}
          <select value={clockId} onChange={e => setClockId(e.target.value)} style={iStyle}>
            {clocks.length === 0 && <option value="">No clocks yet</option>}
            {clocks.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => setShowNewClock(true)} style={{ ...iStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Plus size={13} /> New Clock
          </button>
          {dirty && (
            <button onClick={saveOrder} disabled={saving} style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: '#2f81f7', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {saving ? 'Saving…' : 'Save Order'}
            </button>
          )}
        </div>
      </div>

      {/* Main layout */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: slot palette + add form */}
        <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid #30363d', padding: '16px 14px', overflowY: 'auto', background: '#161b22' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em', marginBottom: 10 }}>ADD SLOT</div>

          {SLOT_TYPES.map(meta => (
            <PaletteBlock
              key={meta.type}
              meta={meta}
              onAdd={() => { setAddType(meta.type); }}
            />
          ))}

          {/* Add controls */}
          <div style={{ marginTop: 16, padding: '14px', background: '#1c2333', borderRadius: 10, border: '1px solid #30363d' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', marginBottom: 8 }}>CONFIGURE</div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 4 }}>Type</div>
              <select value={addType} onChange={e => setAddType(e.target.value)} style={{ ...iStyle, width: '100%' }}>
                {SLOT_TYPES.map(m => <option key={m.type} value={m.type}>{m.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 4 }}>Category</div>
              <select value={addCat} onChange={e => setAddCat(e.target.value)} style={{ ...iStyle, width: '100%' }}>
                <option value="">None</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 4 }}>Duration (secs)</div>
              <input
                type="number" value={addDur} onChange={e => setAddDur(e.target.value)}
                placeholder={String(slotMeta(addType).defaultDur)}
                style={{ ...iStyle, width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <button
              onClick={addSlot}
              disabled={!clockId}
              style={{ width: '100%', padding: '8px', borderRadius: 7, border: 'none', background: clockId ? slotMeta(addType).color : '#21262d', color: clockId ? '#fff' : '#484f58', cursor: clockId ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700 }}
            >
              + Add {slotMeta(addType).label}
            </button>
          </div>

          {/* Stats */}
          {slots.length > 0 && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: '#0d1117', borderRadius: 8, border: '1px solid #30363d' }}>
              <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 4 }}>CLOCK STATS</div>
              <div style={{ fontSize: 13, color: '#e6edf3', fontWeight: 600 }}>{slots.length} slots</div>
              <div style={{ fontSize: 12, color: '#8b949e' }}>{fmtSecs(totSecs)} total</div>
              <div style={{ fontSize: 11, color: totMins >= 60 ? '#22c55e' : '#f59e0b', marginTop: 3 }}>
                {totMins >= 60 ? '✓ Full hour' : `${60 - totMins}m short of 1h`}
              </div>
              {/* Type breakdown */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                {SLOT_TYPES.filter(m => slots.some(s => s.slot_type === m.type)).map(m => {
                  const count = slots.filter(s => s.slot_type === m.type).length;
                  return (
                    <span key={m.type} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: m.bg, color: m.color, fontWeight: 700 }}>
                      {count} {m.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Main: timeline */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 20px 20px 10px' }}>
          {!clockId ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#484f58', gap: 12 }}>
              <RotateCw size={36} style={{ opacity: 0.3 }} />
              <p style={{ margin: 0, fontSize: 14 }}>Select or create a clock template to start building</p>
              <button onClick={() => setShowNewClock(true)} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#2f81f7', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Create first clock
              </button>
            </div>
          ) : slots.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#484f58', gap: 10 }}>
              <p style={{ margin: 0, fontSize: 14 }}>Clock is empty — add slots from the left panel</p>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 0 }}>
              {/* Time markers */}
              <TimeMarkers slots={slots} />

              {/* Slot blocks */}
              <div style={{ flex: 1 }}>
                {slots.map((slot, i) => (
                  <SlotBlock
                    key={slot.id}
                    slot={slot}
                    index={i}
                    isDragging={dragIdx === i}
                    isOver={overIdx === i && dragIdx !== i}
                    categories={categories}
                    onDelete={deleteSlot}
                    onDragStart={onDragStart}
                    onDragEnter={onDragEnter}
                    onDragEnd={onDragEnd}
                  />
                ))}
                {/* Drop zone at end */}
                <div
                  onDragEnter={() => setOverIdx(slots.length)}
                  onDragOver={e => e.preventDefault()}
                  style={{
                    height: 32, borderTop: overIdx === slots.length && dragIdx !== null ? '2px solid #2f81f7' : '2px solid transparent',
                    borderRadius: 4, transition: 'border-color 0.1s',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {showNewClock && (
        <NewClockModal
          storeId={storeId}
          onCreated={id => { setShowNewClock(false); loadClocks(storeId).then(() => setClockId(id)); }}
          onClose={() => setShowNewClock(false)}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
